/**
 * @fileoverview Side-panel legend for cross-form rule connections in the template builder.
 * @module ui-admin/components/template-builder/RuleLinesLegend
 *
 * Responsibilities:
 * - List rule-line inventory with source, target, action, index, and color.
 * - Coordinate rule highlighting with the canvas through hover callbacks.
 * Dependencies: React, lucide-react, template-builder types, buildRuleLineInventory.
 * Architecture: Presentation component; template and form records are supplied by the canvas.
 * Notes: The panel returns null while closed and computes its inventory from the current inputs.
 */
import { useMemo } from 'react';
import { X } from 'lucide-react';
import type { TemplateBuilderTemplate, TemplateBuilderForm } from '../../types/templateTypes';
import { buildRuleLineInventory } from './ruleLinesInventory';

// ---------------------------------------------------------------------------
// RuleLinesLegend — slide-in side panel listing every cross-form rule line
// with its numbered badge. Hover syncs with the RuleConnectionLines overlay
// via the lifted hoveredRuleId state in TemplateCanvas.
// ---------------------------------------------------------------------------

/**
 * @contract RuleLinesLegendProps
 * Purpose: Supplies rule inventory inputs and canvas interaction callbacks.
 * Fields: template, forms, open, onClose, hoveredRuleId, onHoverRule.
 * Rules: Hover callbacks use a rule ID or null to clear highlighting.
 * Sync: Presentation-only; the parent owns template state and hover synchronization.
 */
export interface RuleLinesLegendProps {
  template: TemplateBuilderTemplate;
  forms: Map<string, TemplateBuilderForm>;
  open: boolean;
  onClose: () => void;
  hoveredRuleId: string | null;
  onHoverRule: (ruleId: string | null) => void;
}

/**
 * @component RuleLinesLegend
 * Purpose: Explain and highlight cross-form rule lines in the template canvas.
 * Props: Template, form map, open state, close callback, and hovered-rule state callbacks.
 * Behavior: Lists show/require and hide rules; hovering a rule synchronizes canvas highlighting.
 * Data Source: local (template and form data supplied by the parent).
 * Constraints: The parent owns panel visibility and the active hover state.
 * Performance: Memoizes rule inventory from the template and form map.
 */
export function RuleLinesLegend({
  template,
  forms,
  open,
  onClose,
  hoveredRuleId,
  onHoverRule,
}: RuleLinesLegendProps) {
  const inventory = useMemo(() => buildRuleLineInventory(template, forms), [template, forms]);

  if (!open) return null;

  const getFormName = (formId: string) => forms.get(formId)?.name ?? formId;

  return (
    <div
      className="absolute right-10 top-4 z-40 w-72 bg-white border border-gray-200 rounded-lg shadow-xl flex flex-col max-h-[70vh]"
      role="region"
      aria-label="Rule connection lines legend"
    >
      <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50 rounded-t-lg">
        <span className="text-xs font-semibold text-gray-700">Rule Connections</span>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600"
          aria-label="Close legend"
        >
          <X size={14} />
        </button>
      </div>

      {inventory.length === 0 ? (
        <div className="p-4 text-xs text-gray-500">
          No cross-form rules. Add rules via the Rules panel to see connections here.
        </div>
      ) : (
        <ul className="flex-1 overflow-y-auto divide-y divide-gray-100">
          {inventory.map((line) => {
            const isHovered = hoveredRuleId === line.ruleId;
            return (
              <li
                key={`${line.ruleId}-${line.index}`}
                onMouseEnter={() => onHoverRule(line.ruleId)}
                onMouseLeave={() => onHoverRule(null)}
                className={`flex items-start gap-2 px-3 py-2 text-xs cursor-default transition-colors ${
                  isHovered ? 'bg-gray-50' : ''
                }`}
              >
                <span
                  className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-semibold text-white shrink-0"
                  style={{ backgroundColor: line.color }}
                >
                  {line.index}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-gray-800 truncate">{line.ruleName || `Rule ${line.index}`}</p>
                  <p className="text-gray-500 truncate">
                    <span className="text-gray-700">{getFormName(line.sourceFormId)}</span>
                    <span className="mx-1 text-gray-400">→</span>
                    <span className="text-gray-700">{getFormName(line.targetFormId)}</span>
                  </p>
                  <p className="text-[10px] mt-0.5" style={{ color: line.color }}>
                    {line.isHide ? 'Hide action' : 'Show / Require'}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="px-3 py-2 border-t bg-gray-50 rounded-b-lg text-[10px] text-gray-500 flex items-center gap-3">
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-0.5 bg-green-500 rounded-sm" /> Show
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-0.5 bg-red-500 rounded-sm" style={{ borderTop: '1px dashed' }} /> Hide
        </span>
      </div>
    </div>
  );
}

export default RuleLinesLegend;
